/**
 * // DEMO ADAPTER — no backend field yet
 *
 * The real membership-admin contract (apps/api/src/membership/membership-admin.controller.ts,
 * packages/auth/src/membership-admin/service.ts) only ever returns, per
 * member: a signed `memberRef`, `membershipStatus` (pending/active/disabled),
 * `roleDisplayNames`, and — only when Auth/Profile already happens to store
 * them — an optional `displayName`/`loginIdentifier`. There is no name,
 * email, department, or richer invite-status enum
 * (دعوت‌نشده/دعوت ارسال‌شده/پذیرفته‌شده/منقضی‌شده) field anywhere in that
 * response, and no resend/cancel-invite endpoint.
 *
 * Per the product brief's "Important direction" (ship the full intended
 * UX even where the backend contract is incomplete, isolated in its own
 * clearly named module, never silently blended into a component as if it
 * came from the API): this module layers those presentation-only fields on
 * top of real membership rows, keyed by the row's real `memberRef`. It is
 * persisted only in this browser's `localStorage`, is never read by any
 * other tenant/viewer, and is NEVER sent to the API as if it were real
 * account data — every write here stays client-side.
 *
 * `TODO(backend): a real team-directory contract needs at least
 * name/email/department fields on the account or membership record, a
 * dedicated invite-status enum (or a `expiresAt` + resend counter surfaced
 * per row, not only via the separate `GET .../invitations/:inviteId`), and
 * a cancel-invite endpoint (`DELETE .../invitations/:inviteId` or similar).`
 */

export interface DemoTeamRecord {
  /** DEMO ADAPTER — no backend field yet. Presentational only. */
  readonly displayName?: string;
  /** DEMO ADAPTER — no backend field yet. Presentational only. */
  readonly email?: string;
  /** DEMO ADAPTER — no backend field yet. Presentational only. */
  readonly department?: string;
  /** DEMO ADAPTER — job title / organizational position. */
  readonly jobTitle?: string;
  /** DEMO ADAPTER — assigned module permissions. */
  readonly permissions?: readonly string[];
  /**
   * DEMO ADAPTER — "لغو دعوت" has no backend endpoint at all (no
   * `DELETE .../invitations/:inviteId`). This flag is a local-only,
   * per-browser affordance: it never reaches the server, and the UI must
   * always show it as a demo action, never as a real cancellation the
   * invited person would actually see.
   */
  readonly cancelledLocally?: boolean;
  /**
   * Real login identifier (phone number) submitted with the invite, cached
   * because the list endpoint never returns it back, but "ارسال دعوت مجدد"
   * needs the real identifier to make a real, idempotent-safe re-POST to
   * `.../invitations`.
   */
  readonly knownLoginIdentifier?: string;
}

type DemoTeamDirectory = Record<string, DemoTeamRecord>;

function storageKey(tenantSlug: string): string {
  return `sk-demo-team-directory:${tenantSlug}`;
}

function readAll(tenantSlug: string): DemoTeamDirectory {
  try {
    const raw = window.localStorage.getItem(storageKey(tenantSlug));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as DemoTeamDirectory) : {};
  } catch {
    // Private browsing / storage disabled / corrupt JSON — demo layer is
    // best-effort presentation only, never load-bearing.
    return {};
  }
}

function writeAll(tenantSlug: string, data: DemoTeamDirectory): void {
  try {
    window.localStorage.setItem(storageKey(tenantSlug), JSON.stringify(data));
  } catch {
    // Ignore — see readAll.
  }
}

/** DEMO ADAPTER — reads this browser's local presentational overlay for one member row. */
export function getDemoTeamRecord(tenantSlug: string, memberRef: string): DemoTeamRecord {
  return readAll(tenantSlug)[memberRef] ?? {};
}

/** DEMO ADAPTER — merges a partial patch into this browser's local overlay for one member row. */
export function upsertDemoTeamRecord(
  tenantSlug: string,
  memberRef: string,
  patch: Partial<DemoTeamRecord>,
): DemoTeamRecord {
  const all = readAll(tenantSlug);
  const next = { ...(all[memberRef] ?? {}), ...patch };
  all[memberRef] = next;
  writeAll(tenantSlug, all);
  return next;
}

export type EffectiveInviteStatus = 'invited' | 'accepted' | 'expired_or_cancelled';

export const INVITE_STATUS_LABEL: Record<EffectiveInviteStatus, string> = {
  invited: 'دعوت ارسال‌شده',
  accepted: 'پذیرفته‌شده',
  expired_or_cancelled: 'منقضی‌شده',
};

/**
 * Combines the real `membershipStatus` (pending/active/disabled) with the
 * demo-only local cancel flag into the brief's 4-state invite vocabulary.
 * "دعوت‌نشده" has no row-level meaning here (every row already has an
 * account, so it was always invited) — it only describes the empty
 * invite-form state, handled separately in the page itself.
 */
export function effectiveInviteStatus(
  membershipStatus: string,
  demo: DemoTeamRecord,
): EffectiveInviteStatus {
  if (membershipStatus === 'pending') {
    return demo.cancelledLocally ? 'expired_or_cancelled' : 'invited';
  }
  // active or disabled both mean the account was activated at some point.
  return 'accepted';
}
